import { File } from '../files/shared/file.model';
import { Faq } from '../faqs/shared/faqs.model';
import { Sellable } from './sellable';
import { CaseTransform } from './case-transform';
import { Addon } from '@galaxy/marketplace-apps/v1';
import { Restrictions } from '../restrictions/shared/restrictions.model';

const COLOR_CODES = ['#EF5350', '#42A5F5', '#66BB6A', '#FFA726', '#AB47BC', '#FFCA28', '#EC407A', '#26C6DA', '#FF7B57'];

const DEFAULT_COLOR = '#808080';

export class Product extends Sellable {
  partnerId: string;
  productId: string;
  editionId: string;
  editionName: string;
  usesEditions: boolean;
  name: string;
  description: string;
  tagline: string;
  iconUrl: string;
  headerImageUrl: string;
  entryUrl: string;
  screenshotUrls: string[];
  pdfUploadUrls: string[];
  lmiCategories: string[];
  keySellingPoints: string[];
  faqs: Faq[];
  needHelp: string;
  wholesalePrice: number;
  setupFee: number;
  usesCustomPricing: boolean;
  currency: string;
  billingFrequency: string;
  billingId: string;
  isArchived: boolean;
  isSuspended: boolean;
  addons: Addon[] = [];
  files: File[];
  restrictions: Restrictions;
  trialConfiguration: TrialConfigurationInterface;
  videos: string[];
  static fromApi(data: any): Product {
    const newProduct = {};
    data = data || {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const newKey = CaseTransform.snakeToCamelCase(key);
        newProduct[newKey] = data[key];
      }
    }
    return new Product(newProduct);
  }
  constructor(data?: any) {
    super();
    data = data || {};
    this.pricing = data.pricing;
    this.productId = data.productId;
    this.partnerId = data.partnerId;
    this.editionId = data.editionId;
    this.editionName = data.editionName;
    this.usesEditions = data.usesEditions;
    this.name = data.name;
    this.description = data.description;
    this.tagline = data.tagline;
    this.iconUrl = data.iconUrl;
    this.headerImageUrl = data.headerImageUrl;
    this.entryUrl = data.entryUrl;
    this.screenshotUrls = data.screenshotUrls;
    this.videos = data.vidoes;
    this.pdfUploadUrls = data.pdfUploadUrls;
    this.lmiCategories = data.lmiCategories;
    this.keySellingPoints = data.keySellingPoints;
    this.faqs = data.faqs;
    this.needHelp = data.needHelp;
    this.wholesalePrice = data.wholesalePrice;
    this.setupFee = data.setupFee;
    this.usesCustomPricing = data.usesCustomPricing;
    this.currency = data.currency;
    this.billingFrequency = data.billingFrequency;
    this.isArchived = data.isArchived;
    this.isSuspended = data.isSuspended;
    this.addons = data.addons;
    this.restrictions = data.restrictions;
    this.billingId = data.billingId;
    this.trialConfiguration = data.trialConfiguration;
    if (Object.prototype.hasOwnProperty.call(data, 'pdfUploadUrls')) {
      this.files = this.pdfUploadUrls ? this.pdfUploadUrls.map((url) => new File(url)) : null;
    }
  }

  get iconColor(): string {
    // determine an icon color for a product with no icon by using the product name
    if (!this.name) {
      return DEFAULT_COLOR;
    }

    let nameSum = 0;
    for (let i = 0; i < this.name.length; i++) {
      nameSum += this.name[i].charCodeAt(0);
    }

    const index = nameSum % COLOR_CODES.length;
    return COLOR_CODES[index];
  }

  getLmiCategoryNames(): string[] {
    const lmiCategories = this.lmiCategories || [];
    return lmiCategories.map((lmiCategory) =>
      CaseTransform.lowerToTitleCase(lmiCategory.toLowerCase().replace(/_/g, ' ')),
    );
  }
}

export class ProductWithParent extends Product {
  parentId: string;
  parentName: string;
  parentIconUrl: string;
  multipleActivations: boolean;

  static fromApi(data: any): ProductWithParent {
    const newProduct = {};
    data = data || {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const newKey = CaseTransform.snakeToCamelCase(key);
        newProduct[newKey] = data[key];
      }
    }
    return new ProductWithParent(newProduct);
  }
  constructor(data?: any) {
    super(data);
    this.parentId = data.parentId;
    this.parentName = data.parentName;
    this.parentIconUrl = data.parentIconUrl;
    this.multipleActivations = data.multipleActivations || false;
  }
}

export interface TrialConfigurationInterface {
  trialEnabled: boolean;
  trialDuration: number;
}
